import { useEffect, useState } from 'react';
import type { Routemize_DTOs_CompanyLocationDto } from '@/api/models/Routemize_DTOs_CompanyLocationDto';
import { GeocodingService } from '@/services/geocodingService';
import {
  formatCompanyLocationAddress,
  getCompanyLocationCoordinates,
} from '@/utils/companyLocationGeo';
import { useGoogleMapsReady } from '@/hooks/useGoogleMapsReady';

/**
 * Resolve map center for a company location — uses stored lat/lng when valid,
 * otherwise geocodes the formatted business address once Maps is ready.
 */
export function useCompanyLocationMapCenter(
  location: Routemize_DTOs_CompanyLocationDto | null | undefined,
  enabled = true,
): { center: { lat: number; lng: number } | null; isLoading: boolean } {
  const { isReady: mapsReady } = useGoogleMapsReady();
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const locationId = location?.id ?? null;
  const storedCoords = getCompanyLocationCoordinates(location);
  const address = location ? formatCompanyLocationAddress(location) : '';

  useEffect(() => {
    if (!enabled || !location) {
      setCenter(null);
      setIsLoading(false);
      return;
    }

    if (storedCoords) {
      setCenter(storedCoords);
      setIsLoading(false);
      return;
    }

    if (!address) {
      setCenter(null);
      setIsLoading(false);
      return;
    }

    if (!mapsReady) {
      setCenter(null);
      setIsLoading(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    GeocodingService.geocodeAddress(address)
      .then((result) => {
        if (cancelled) return;
        setCenter(result ? { lat: result.lat, lng: result.lng } : null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, locationId, storedCoords?.lat, storedCoords?.lng, address, mapsReady]);

  return { center, isLoading };
}
